import { UseQueryResult, useQuery } from 'react-query';
import ShieldGPSDeviceStatsAxiosClient from '../lib/ShieldGPSDeviceStatsAxiosClient';

export const DEVICE_TELEMETRY_BUCKET_MS = 15 * 60 * 1000;
export const DEVICE_TELEMETRY_REFRESH_MS = 60 * 1000;
export const DEVICE_TELEMETRY_QUERY_OPTIONS = {
  retry: false,
  staleTime: DEVICE_TELEMETRY_REFRESH_MS,
  cacheTime: 5 * DEVICE_TELEMETRY_REFRESH_MS,
  refetchInterval: DEVICE_TELEMETRY_REFRESH_MS,
  refetchIntervalInBackground: false,
} as const;

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export interface DeviceTelemetryBucketResponse {
  key: string;
  packetCount?: number;
  outOfOrderCount?: number;
  largePositionGapCount?: number;
  packetCountBreakdown?: Record<string, number>;
  averageLagMs?: number;
  battery?: string | null;
}

export interface DeviceTelemetryResponse {
  imei?: string;
  startAt?: string;
  endAt?: string;
  score?: number;
  buckets: DeviceTelemetryBucketResponse[];
}

export interface DeviceTelemetryBucket {
  key: string;
  packetCount: number;
  positionPacketCount: number;
  outOfOrderCount: number;
  largePositionGapCount: number;
  averageLagMs: number;
  batteryVoltage?: number;
  batteryPercent?: number;
}

export interface DeviceTelemetry {
  imei: string;
  startAt?: string;
  endAt?: string;
  score: number;
  buckets: DeviceTelemetryBucket[];
}

export interface DeviceTelemetryWindow {
  startAt: Date;
  endAt: Date;
}

export interface DeviceTelemetryRequest {
  imei: string;
  startAt: Date;
}

export interface UseDeviceTelemetryRequest {
  imei: string;
  durationDays: number;
}

const finiteNumberOrZero = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const calculateDeviceTelemetryWindow = (
  durationDays: number,
  now: Date = new Date(),
): DeviceTelemetryWindow => {
  if (!Number.isFinite(durationDays) || durationDays <= 0) {
    throw new Error('Telemetry duration must be greater than zero.');
  }

  const endAtMs = Math.floor(now.getTime() / DEVICE_TELEMETRY_BUCKET_MS) * DEVICE_TELEMETRY_BUCKET_MS;

  return {
    startAt: new Date(endAtMs - durationDays * ONE_DAY_MS),
    endAt: new Date(endAtMs),
  };
};

export const buildDeviceTelemetryPath = ({ imei, startAt }: DeviceTelemetryRequest): string => {
  const query = new URLSearchParams({ startAt: startAt.toISOString() });
  return `/devices/${encodeURIComponent(imei)}?${query.toString()}`;
};

export const buildDeviceTelemetryUrl = (request: DeviceTelemetryRequest): string => {
  const baseUrl = ShieldGPSDeviceStatsAxiosClient.defaults.baseURL;
  if (!baseUrl) {
    throw new Error('REACT_APP_DEVICE_STATS_API_BASE_URL is not configured.');
  }

  return new URL(buildDeviceTelemetryPath(request), baseUrl).toString();
};

export const normalizeDeviceTelemetryBuckets = (
  buckets: DeviceTelemetryBucketResponse[],
): DeviceTelemetryBucket[] => buckets.map((bucket, index) => {
  if (!isRecord(bucket) || typeof bucket.key !== 'string') {
    throw new Error(`Invalid telemetry bucket at index ${index}.`);
  }

  const packetCountBreakdown = isRecord(bucket.packetCountBreakdown)
    ? bucket.packetCountBreakdown
    : {};
  const batteryParts = typeof bucket.battery === 'string'
    ? bucket.battery.split(':').map(Number)
    : [];
  const batteryVoltage = Number.isFinite(batteryParts[0]) ? batteryParts[0] : undefined;
  const batteryPercent = Number.isFinite(batteryParts[1]) ? batteryParts[1] : undefined;

  return {
    key: bucket.key,
    packetCount: finiteNumberOrZero(bucket.packetCount) > 0 ? 1 : 0,
    positionPacketCount: finiteNumberOrZero(packetCountBreakdown.pos),
    outOfOrderCount: finiteNumberOrZero(bucket.outOfOrderCount),
    largePositionGapCount: finiteNumberOrZero(bucket.largePositionGapCount),
    averageLagMs: finiteNumberOrZero(bucket.averageLagMs),
    batteryVoltage,
    batteryPercent,
  };
});

export const normalizeDeviceTelemetryResponse = (
  response: unknown,
  requestedImei: string,
): DeviceTelemetry => {
  if (!isRecord(response) || !Array.isArray(response.buckets)) {
    throw new Error('Invalid telemetry response: expected a buckets array.');
  }

  return {
    imei: typeof response.imei === 'string' ? response.imei : requestedImei,
    startAt: typeof response.startAt === 'string' ? response.startAt : undefined,
    endAt: typeof response.endAt === 'string' ? response.endAt : undefined,
    score: finiteNumberOrZero(response.score),
    buckets: normalizeDeviceTelemetryBuckets(response.buckets as DeviceTelemetryBucketResponse[]),
  };
};

export const deviceTelemetryQueryKey = (request: DeviceTelemetryRequest) => [
  'device-telemetry',
  request.imei,
  request.startAt.toISOString(),
] as const;

export const getDeviceTelemetry = async (request: DeviceTelemetryRequest): Promise<DeviceTelemetry> => {
  const response = await ShieldGPSDeviceStatsAxiosClient.get<DeviceTelemetryResponse>(
    buildDeviceTelemetryPath(request),
  );

  return normalizeDeviceTelemetryResponse(response.data, request.imei);
};

export type UseDeviceTelemetryResult = UseQueryResult<DeviceTelemetry, Error> & {
  window: DeviceTelemetryWindow;
  rawDataUrl: string;
};

export const useDeviceTelemetry = ({
  imei,
  durationDays,
}: UseDeviceTelemetryRequest): UseDeviceTelemetryResult => {
  const window = calculateDeviceTelemetryWindow(durationDays);
  const request = { imei, startAt: window.startAt };
  const query = useQuery<DeviceTelemetry, Error>(
    deviceTelemetryQueryKey(request),
    () => getDeviceTelemetry(request),
    {
      ...DEVICE_TELEMETRY_QUERY_OPTIONS,
      enabled: Boolean(imei),
    },
  );

  return {
    ...query,
    window,
    rawDataUrl: buildDeviceTelemetryUrl(request),
  };
};
